import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import useAcvDatadog from '@/composables/useAcvDatadog';

/**
 * Nuxt plugin for initializing the Google Maps JavaScript API.
 *
 * This plugin loads the Google Maps API once at application startup using the modern
 * `importLibrary` approach. It handles loading failures gracefully with fallback logic
 * for intermittent errors common on mobile browsers (particularly iOS Safari).
 *
 * The API promise is provided globally via `$googleMaps.apiPromise` and can be
 * accessed in any component using `useNuxtApp()`.
 *
 * @example
 * In a component
 * const { $googleMaps } = useNuxtApp();
 * <GoogleMap :api-promise="$googleMaps.apiPromise" />
 */
export default defineNuxtPlugin({
  parallel: true,
  setup() {
    const { addRumError, addRumAction } = useAcvDatadog();
    const config = useRuntimeConfig();

    // Configure Google Maps API options
    setOptions({
      key: config.public.MAP_API_KEY,
      v: 'weekly',
    });

    // Load required Google Maps libraries
    const apiPromise = Promise.all([importLibrary('places'), importLibrary('marker')])
      .then(() => window.google)
      .catch((error) => {
        if (typeof window.google !== 'undefined' && typeof window.google.maps !== 'undefined') {
          addRumAction('googleMaps plugin ==> importLibrary threw but Google Maps is available');
          return window.google;
        }

        addRumError(new Error(error), {
          context: 'googleMaps plugin ==> Google Maps failed to load',
          apiKeyPresent: !!config.public.MAP_API_KEY,
          userAgent: navigator.userAgent,
          errorMessage: error.message,
          errorType: error.constructor.name,
        });

        // Re-throw to maintain promise rejection chain
        throw error;
      });

    return {
      provide: {
        googleMaps: {
          apiPromise,
        },
      },
    };
  },
});
